import * as React from 'react';
import {Flex, List, Loader, Text, TrashCanIcon} from '@fluentui/react-northstar';
import './draftMessages.scss';
import Overflow from '../OverFlow/draftMessageOverflow';
import {withRouter, WithRouterProps} from "services/TelemetryService/withRouter.hook";
import {IDraftMessageSummary} from '../../Interfaces/IDraftMessageSummary';
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";
import {deleteDraftNotification, getDraftNotificationsSummaries} from "../../apis/messageListApi";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import {IMessageChannel} from "../../Interfaces/Message";

export interface IMessageProps extends WithRouterProps {
    channelId: string;
    countOfDrafts: (count: number) => void;
    refreshMessages: () => void;
    onRef: (ref: IDraftMessagesRef) => void;
    onMessagesFetch: (messages: IDraftMessageSummary[]) => void;
    filteredAuthors: string[];
    filteredGPTs: string[];
}

export interface IDraftMessagesRef {
    messages: IDraftMessageSummary[];
    refreshMessages: () => Promise<void>;
}

export interface IMessageState {
    messages: IDraftMessageSummary[];
    itemsAccount: number;
    loader: boolean;
    teamsTeamId?: string;
    teamsChannelId?: string;
    messageToDeleteId: string;
    isDeleting: boolean;
}

class DraftMessages extends React.Component<IMessageProps, IMessageState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    private _isMounted: boolean = false;

    private columnsWidth = {
        title: "calc(66% + 30px)",
        channel: "14%",
        author: "20%",
    }

    constructor(props: IMessageProps) {
        super(props);

        this.state = {
            messages: new Array<IDraftMessageSummary>(),
            itemsAccount: 0,
            loader: true,
            teamsTeamId: "",
            teamsChannelId: "",
            messageToDeleteId: "",
            isDeleting: false,
        };

        this.handleDeleteMessage = this.handleDeleteMessage.bind(this);
        this.handleOpenDeleteDialog = this.handleOpenDeleteDialog.bind(this);
        this.handleDuplicateMessage = this.handleDuplicateMessage.bind(this);
        this.handleSendMessage = this.handleSendMessage.bind(this);
        this.refreshDraftMessages = this.refreshDraftMessages.bind(this);
    }

    async componentDidMount() {
        this._isMounted = true;
        await this.refreshDraftMessages();
        if (!this._isMounted) return;
        this.setState({
            loader: false,
            teamsTeamId: this.context.teams.teamId,
            teamsChannelId: this.context.teams.channelId,
        });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    async refreshDraftMessages() {
        let messageSummaries = await getDraftNotificationsSummaries();
        messageSummaries = messageSummaries.reverse();
        this.props.countOfDrafts(messageSummaries.length);
        if (!this._isMounted) return;
        this.setState({messages: messageSummaries});
        this.props.onRef({
            messages: messageSummaries,
            refreshMessages: this.refreshDraftMessages
        });
        this.props.onMessagesFetch(messageSummaries);
    }

    public componentDidUpdate(prevProps: IMessageProps, prevState: IMessageState) {
        if (this.state.messages.length != prevState.messages.length) {
            this.props.countOfDrafts(this.state.messages.length)
        }
    }

    public handleOpenDeleteDialog(id: string) {
        if (!this._isMounted) return;
        this.setState({messageToDeleteId: id});
    }

    public async handleDeleteMessage() {
        if (!this.state.messageToDeleteId) return;
        this.setState({isDeleting: true});
        try {
            await deleteDraftNotification(this.state.messageToDeleteId);
            await this.refreshDraftMessages();
            if (!this._isMounted) return;
            this.setState({messageToDeleteId: "", isDeleting: false});
        } catch (error) {
            return error;
        }
    }

    public async handleDuplicateMessage() {
        await this.refreshDraftMessages();
    }

    public async handleSendMessage() {
        this.props.refreshMessages();
        await this.refreshDraftMessages();
    }

    public getFilteredMessages = () => {
        return this.state.messages.filter(message => {
            if (this.props.filteredAuthors.length > 0 && !this.props.filteredAuthors.includes(message.authorName)) {
                return false;
            }
            if (this.props.filteredGPTs.length > 0 && message.gptId && !this.props.filteredGPTs.includes(message.gptId)) {
                return false;
            }
            return true;
        });
    }

    public render(): JSX.Element {
        if (this.state.loader) return (
            <Flex>
                <Loader size={"small"} labelPosition={"end"} label={languageObject.translate("LoadingMessages")}/>
            </Flex>
        );

        let keyCount = 0;
        const processItem = (message: any) => {
            const isGpt = message.channel === IMessageChannel.Channel_GPTPro;
            const channelName = languageObject.translate(IMessageChannel[message.channel]);
            const gptName = isGpt ? this.context.gpts.list.find(g => g.id === message.gptId)?.shortName : "";
            keyCount++;
            return {
                key: keyCount,
                className: "message-list-item",
                content: (
                    <Flex vAlign="center" gap="gap.small">
                        <Flex fill vAlign={"center"} className={"overflow-hidden"}
                              styles={{width: this.columnsWidth.title}}>
                            <Text truncated content={message.title}/>
                        </Flex>
                        <Flex fill vAlign={"center"} className={"overflow-hidden"}
                              styles={{width: this.columnsWidth.channel}}>
                            <Text
                                truncated
                                content={
                                    languageObject.translate(IMessageChannel[message.channel]) +
                                    (isGpt && gptName !== channelName && gptName !== undefined ? ` (${gptName})` : '')
                                }
                            />
                        </Flex>
                        <Flex fill vAlign={"center"} className={"overflow-hidden"}
                              styles={{width: this.columnsWidth.author}}>
                            <Text truncated content={message.authorName}/>
                        </Flex>
                        <Flex vAlign={"center"} styles={{minWidth: "32px"}}>
                            <Overflow
                                message={message} title="" channelId={this.props.channelId}
                                handleDelete={this.handleOpenDeleteDialog}
                                onDuplicateMessage={this.handleDuplicateMessage}
                                onMessageSent={this.handleSendMessage}
                            />
                        </Flex>
                    </Flex>
                ),
                onClick: () => this.props.navigate("/newmessage/" + message.id),
            };
        };

        const label = this.processLabels();
        const outList = this.getFilteredMessages().map(processItem);
        const allDraftMessages = [...label, ...outList];

        return this.state.messages.length === 0 ?
            <div className="results">{languageObject.translate("EmptyDraftMessages")}</div>
            :
            <>
                <List selectable items={allDraftMessages} className="list"/>
                {this.renderConfirmRemoveMessage()}
            </>
    }

    private renderConfirmRemoveMessage = () => (
        <ConfirmDialog
            show={!!this.state.messageToDeleteId}
            title={languageObject.translate("RemoveMessage")}
            subtitle={this.state.messages.find(m => m.id === this.state.messageToDeleteId)?.title}
            content={languageObject.translate("ConfirmRemoveMessage")}
            onCancel={() => this.setState({messageToDeleteId: ""})}
            onConfirm={this.handleDeleteMessage}
            loading={this.state.isDeleting}
            button={{
                icon: <TrashCanIcon outline/>,
                title: languageObject.translate("Remove"),
                className: "delete_button",
            }}
        />
    )

    private processLabels = () => {
        return [{
            key: "labels",
            content: (
                <Flex vAlign="center" fill gap="gap.small">
                    <Flex styles={{width: this.columnsWidth.title}}>
                        <Text
                            truncated
                            weight="bold"
                            content={languageObject.translate("TitleText")}
                        />
                    </Flex>
                    <Flex fill vAlign={"center"} styles={{width: this.columnsWidth.channel}}>
                        <Text
                            truncated
                            content={languageObject.translate("Channel")}
                            weight="bold"
                        />
                    </Flex>
                    <Flex fill vAlign={"center"} styles={{width: this.columnsWidth.author}}>
                        <Text
                            truncated
                            weight="bold"
                            content={languageObject.translate("Author")}
                        />
                    </Flex>
                    <Flex styles={{minWidth: "32px"}}>
                    </Flex>
                </Flex>
            ),
        }];
    }
}

export default withRouter(DraftMessages);